import React, { useState } from "react";
import { Col, Form, Row, Table } from "react-bootstrap";
import { useLoaderData } from "react-router";
import { IGameLog } from "../../types/api";
import { getGame, getGameLogs } from "../../lib/api";
import { defaultDateFormat } from "../../lib/utils";
import { useAppSelector } from "../../hooks/reduxHooks";
import Select from "react-select";
import {
    globalActions,
    selectAgeLevels,
    selectSetting,
} from "../../features/global/globalSlice";
import { store } from "../..";
import DatePicker from "react-datepicker";
import GameLogTable from "./GameLogTable";
import {
    leagueManagementActions,
    selectLeagues,
    selectYears,
} from "../../features/leaguemgmt/leagueManagementSlice";

interface Filters {
    gameDate?: Date;
    logDate?: Date;
    ageLevels: {
        options?: Array<string>;
        filter?: Array<string>;
    };
    action: {
        options?: Array<string>;
        filter?: Array<string>;
    };
    logCategory: {
        options?: Array<string>;
        filter?: Array<string>;
    };
    team?: string;
    year: {
        options: Array<number>;
        filter: number | undefined;
    };
}

export const loader = async ({ params }: any) => {
    const { gameId } = params;
    const { years, leagues } = store.getState().leaguemanagement;
    const ageLevels = store.getState().global.ageLevels;

    const promises = [];

    !years && promises.push(store.dispatch(leagueManagementActions.getYears()));
    !leagues.length &&
        promises.push(store.dispatch(leagueManagementActions.getLeagues()));
    !ageLevels.length &&
        promises.push(store.dispatch(globalActions.getAgeLevels()));

    let logs: IGameLog[];
    let header: string;

    if (gameId) {
        const [{ game }, { logs: gameLogs }] = await Promise.all([
            getGame(gameId),
            getGameLogs(gameId),
            ...promises,
        ]);

        logs = gameLogs;
        const leagues = selectLeagues(store.getState());
        const league = leagues.find(l => l.id === game.league);
        const home = game.home.name || "TBD";
        const visitor = game.visitor.name || "TBD";
        header = `${league?.ageLevel}${league?.designation ? ` ${league.designation}` : ""}${league?.direction ? ` ${league.direction}` : ""} - ${visitor} @ ${home} Game Log`;
    } else {
        const [{ logs: gameLogs }] = await Promise.all([
            getGameLogs(),
            ...promises,
        ]);
        logs = gameLogs;
        header = "Game Logs";
    }

    return {
        gameId,
        logs,
        header,
    };
};

const GameLogs = (): React.JSX.Element => {
    const { logs, gameId } = useLoaderData() as {
        gameId?: number;
        logs: IGameLog[];
    };

    const [gameLogs, setGameLogs] = useState<IGameLog[]>(logs);
    const ageLevels = useAppSelector(selectAgeLevels);
    const years = useAppSelector(selectYears);
    const currentYear = useAppSelector(state =>
        selectSetting(state, "majorybl_current_league_year"),
    );

    const [filters, setFilters] = useState<Filters>({
        team: undefined,
        ageLevels: {
            options: Array.from(new Set(ageLevels.map(a => a.ageLevel))),
            filter: undefined,
        },
        action: {
            options: Array.from(new Set(gameLogs.map(l => l.action))).sort(),
            filter: undefined,
        },
        gameDate: undefined,
        logDate: undefined,
        logCategory: {
            options: Array.from(new Set(gameLogs.map(l => l.category))).sort(),
            filter: undefined,
        },
        year: {
            options: years
                ? Array.from(Object.keys(years)).map(y => parseInt(y))
                : [],
            filter: parseInt(currentYear?.setting) || undefined,
        },
    });

    const handleYearChange = async (year: number | undefined) => {
        const { logs } = await getGameLogs(undefined, year);
        setGameLogs(logs);
    };

    const filteredLogs = gameLogs
        .filter(l =>
            filters.team
                ? l.game.home.name
                      .toLowerCase()
                      .includes(filters.team.toLowerCase()) ||
                  l.game.visitor.name
                      .toLowerCase()
                      .includes(filters.team.toLowerCase())
                : true,
        )
        .filter(l =>
            filters.ageLevels.filter
                ? filters.ageLevels.filter.includes(l.game.ageLevel.ageLevel)
                : true,
        )
        .filter(l =>
            filters.action.filter
                ? filters.action.filter.includes(l.action)
                : true,
        )
        .filter(l =>
            filters.logCategory.filter
                ? filters.logCategory.filter.includes(l.category)
                : gameId
                  ? l.category !== "Umpire"
                  : true,
        )
        .filter(l => {
            return filters.logDate
                ? defaultDateFormat.format(new Date(l.date)) ===
                      defaultDateFormat.format(filters.logDate)
                : true;
        })
        .filter(l => {
            return filters.gameDate
                ? defaultDateFormat.format(new Date(l.game.date)) ===
                      defaultDateFormat.format(filters.gameDate)
                : true;
        });

    const umpireLogs = gameLogs
        .filter(l =>
            filters.team
                ? l.game.home.name
                      .toLowerCase()
                      .includes(filters.team.toLowerCase()) ||
                  l.game.visitor.name
                      .toLowerCase()
                      .includes(filters.team.toLowerCase())
                : true,
        )
        .filter(l =>
            filters.ageLevels.filter
                ? filters.ageLevels.filter.includes(l.game.ageLevel.ageLevel)
                : true,
        )
        .filter(l =>
            filters.action.filter
                ? filters.action.filter.includes(l.action)
                : true,
        )
        .filter(l => (gameId ? l.category === "Umpire" : true))
        .filter(l => {
            return filters.logDate
                ? defaultDateFormat.format(new Date(l.date)) ===
                      defaultDateFormat.format(filters.logDate)
                : true;
        })
        .filter(l => {
            return filters.gameDate
                ? defaultDateFormat.format(new Date(l.game.date)) ===
                      defaultDateFormat.format(filters.gameDate)
                : true;
        });

    return (
        <section className="gameLogsContent" data-testid="gameLogsContent">
            {!gameId && (
                <section className="gameLogsFilters">
                    <Row>
                        <Col xs={12} sm={6} xl={4} className="my-2">
                            <h5>Age Level:</h5>
                            <Select
                                isSearchable={false}
                                isClearable={true}
                                isMulti={true}
                                options={filters.ageLevels.options?.map(
                                    (a, idx) => {
                                        return {
                                            value: idx,
                                            label: a,
                                        };
                                    },
                                )}
                                onChange={a => {
                                    setFilters(prev => ({
                                        ...prev,
                                        ageLevels: {
                                            ...prev.ageLevels,
                                            filter:
                                                a.length === 0
                                                    ? undefined
                                                    : a?.map(o => o.label),
                                        },
                                    }));
                                }}
                            />
                        </Col>
                        <Col xs={12} sm={6} xl={4} className="my-2">
                            <h5>Team:</h5>
                            <Form.Control
                                type="text"
                                placeholder="Team"
                                onChange={e => {
                                    setFilters(prev => ({
                                        ...prev,
                                        team:
                                            e.target.value.trim() === ""
                                                ? undefined
                                                : e.target.value.trim(),
                                    }));
                                }}
                            />
                        </Col>
                        <Col xs={12} sm={6} xl={2} className="my-2">
                            <h5>Game Date:</h5>
                            <DatePicker
                                selected={
                                    filters.gameDate
                                        ? new Date(filters.gameDate)
                                        : undefined
                                }
                                onChange={d =>
                                    setFilters(prev => ({
                                        ...prev,
                                        gameDate: d || undefined,
                                    }))
                                }
                                isClearable
                                showMonthDropdown
                                showYearDropdown
                                dateFormat="MMMM d, yyyy"
                                dropdownMode="select"
                                className="form-control"
                            />
                        </Col>
                        <Col xs={12} sm={6} xl={2} className="my-2">
                            <h5>Log Date:</h5>
                            <DatePicker
                                selected={
                                    filters.logDate
                                        ? new Date(filters.logDate)
                                        : undefined
                                }
                                onChange={d =>
                                    setFilters(prev => ({
                                        ...prev,
                                        logDate: d || undefined,
                                    }))
                                }
                                isClearable
                                showMonthDropdown
                                showYearDropdown
                                dateFormat="MMMM d, yyyy"
                                dropdownMode="select"
                                className="form-control"
                            />
                        </Col>
                        <Col xs={12} sm={4} className="my-2">
                            <h5>Log Action:</h5>
                            <Select
                                isSearchable={false}
                                isClearable={true}
                                isMulti={true}
                                options={filters.action.options?.map(
                                    (a, idx) => {
                                        return {
                                            value: idx,
                                            label: a,
                                        };
                                    },
                                )}
                                onChange={a => {
                                    setFilters(prev => ({
                                        ...prev,
                                        action: {
                                            ...prev.action,
                                            filter:
                                                a.length === 0
                                                    ? undefined
                                                    : a?.map(o => o.label),
                                        },
                                    }));
                                }}
                            />
                        </Col>
                        <Col xs={12} sm={4} className="my-2">
                            <h5>Log Category:</h5>
                            <Select
                                isSearchable={false}
                                isClearable={true}
                                isMulti={true}
                                options={filters.logCategory.options?.map(
                                    (a, idx) => {
                                        return {
                                            value: idx,
                                            label: a,
                                        };
                                    },
                                )}
                                onChange={a => {
                                    setFilters(prev => ({
                                        ...prev,
                                        logCategory: {
                                            ...prev.logCategory,
                                            filter:
                                                a.length === 0
                                                    ? undefined
                                                    : a?.map(o => o.label),
                                        },
                                    }));
                                }}
                            />
                        </Col>
                        <Col xs={12} sm={4} className="my-2">
                            <h5>League Year:</h5>
                            <Select
                                isSearchable={false}
                                isClearable={true}
                                isMulti={false}
                                options={filters.year.options.map(y => {
                                    return {
                                        value: y,
                                        label: y,
                                    };
                                })}
                                defaultValue={
                                    filters.year.filter
                                        ? {
                                              value: filters.year.filter,
                                              label: filters.year.filter,
                                          }
                                        : undefined
                                }
                                onChange={y => {
                                    setFilters(prev => ({
                                        ...prev,
                                        year: {
                                            ...prev.year,
                                            filter: y?.value,
                                        },
                                    }));
                                    handleYearChange(y?.value);
                                }}
                            />
                        </Col>
                    </Row>
                </section>
            )}
            {gameId && <h3>Game History</h3>}
            <GameLogTable data={filteredLogs} />
            {gameId && <h3>Umpire History</h3>}
            {gameId && <GameLogTable data={umpireLogs} />}
        </section>
    );
};

export default GameLogs;
